class NewsVideoModal {
    constructor() {
        this.openButton = document.querySelector('.btnPlayVideo[data-video-embed]');
        this.modal = document.querySelector('.videoModal');
        this.closeButton = this.modal?.querySelector('.btnCloseVideo');
        this.iframe = this.modal?.querySelector('.videoModalIframe');

        if (!this.openButton || !this.modal || !this.closeButton || !this.iframe) return;

        this.bindEvents();
    }

    bindEvents() {
        this.openButton.addEventListener('click', () => this.open());
        this.closeButton.addEventListener('click', () => this.close());

        this.modal.addEventListener('click', event => {
            if (event.target === this.modal) this.close();
        });

        document.addEventListener('keydown', event => {
            if (event.key === 'Escape' && this.modal.getAttribute('aria-hidden') === 'false') {
                this.close();
            }
        });
    }

    open() {
        this.iframe.src = this.openButton.dataset.videoEmbed;
        this.modal.classList.remove('invisible', 'opacity-0', 'pointer-events-none');
        this.modal.removeAttribute('inert');
        this.modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('overflow-hidden');
        this.closeButton.focus();
    }

    close() {
        this.modal.classList.add('invisible', 'opacity-0', 'pointer-events-none');
        this.modal.setAttribute('inert', '');
        this.modal.setAttribute('aria-hidden', 'true');
        this.iframe.src = '';
        document.body.classList.remove('overflow-hidden');
        this.openButton.focus();
    }
}

class YouTubeThumbnail {
    constructor(image) {
        this.image = image;
        this.fallbackSrc = image.dataset.fallbackSrc;

        if (!this.fallbackSrc) return;

        this.image.addEventListener('error', () => this.useFallback(), { once: true });

        if (this.image.complete && this.image.naturalWidth === 0) {
            this.useFallback();
        }
    }

    useFallback() {
        if (this.image.src === this.fallbackSrc) return;

        this.image.classList.add('scale-[1.02]', 'blur-[2px]');
        this.image.src = this.fallbackSrc;
    }
}

class ArticleTableOfContents {
    constructor(section) {
        this.section = section;
        this.content = section.querySelector('.contentnews');
        this.navigation = section.querySelector('.article-table-of-contents');
        this.list = this.navigation?.querySelector('.article-table-of-contents-list');

        if (!this.content || !this.navigation || !this.list) return;

        this.build();
    }

    build() {
        const headings = [...this.content.querySelectorAll('h1, h2')]
            .filter(heading => heading.textContent.trim());

        if (!headings.length) return;

        const usedIds = new Set(
            [...document.querySelectorAll('[id]')]
                .filter(element => !this.content.contains(element))
                .map(element => element.id)
        );
        const fragment = document.createDocumentFragment();
        this.headings = headings;
        this.links = [];

        headings.forEach((heading, index) => {
            const baseId = heading.id || this.slugify(heading.textContent) || `secao-${index + 1}`;
            heading.id = this.uniqueId(baseId, usedIds);

            const item = document.createElement('li');
            const link = document.createElement('a');

            item.className = heading.tagName === 'H2' ? 'pl-3' : '';
            link.className = 'block border-l-2 border-transparent pl-3 text-sm/150 text-cinza transition-colors hover:border-secondary hover:text-secondary dark:text-darkmode-muted dark:hover:text-white';
            link.href = `#${encodeURIComponent(heading.id)}`;
            link.textContent = heading.textContent.trim();

            item.appendChild(link);
            fragment.appendChild(item);
            this.links.push(link);
        });

        this.list.appendChild(fragment);
        this.navigation.classList.remove('hidden');
        this.bindAnchorNavigation();
        this.bindActiveHeading();
        this.updateActiveHeading();
    }

    bindAnchorNavigation() {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.links.forEach((link, index) => {
            link.addEventListener('click', event => {
                event.preventDefault();

                const heading = this.headings[index];
                heading.scrollIntoView({
                    behavior: reduceMotion.matches ? 'auto' : 'smooth',
                    block: 'start',
                });

                window.history.pushState(null, '', link.href);
            });
        });
    }

    bindActiveHeading() {
        this.ticking = false;
        this.handleViewportChange = () => {
            if (this.ticking) return;

            this.ticking = true;
            window.requestAnimationFrame(() => {
                this.updateActiveHeading();
                this.ticking = false;
            });
        };

        window.addEventListener('scroll', this.handleViewportChange, { passive: true });
        window.addEventListener('resize', this.handleViewportChange, { passive: true });
    }

    updateActiveHeading() {
        const topOffset = 160;
        let activeIndex = 0;

        this.headings.forEach((heading, index) => {
            if (heading.getBoundingClientRect().top <= topOffset) {
                activeIndex = index;
            }
        });

        this.links.forEach((link, index) => {
            const isActive = index === activeIndex;

            link.classList.toggle('border-transparent', !isActive);
            link.classList.toggle('border-secondary', isActive);
            link.classList.toggle('text-cinza', !isActive);
            link.classList.toggle('text-secondary', isActive);
            link.classList.toggle('font-bold', isActive);

            if (isActive) {
                link.setAttribute('aria-current', 'location');
            } else {
                link.removeAttribute('aria-current');
            }
        });
    }

    slugify(value) {
        return value
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    uniqueId(baseId, usedIds) {
        let id = baseId;
        let suffix = 2;

        while (usedIds.has(id)) {
            id = `${baseId}-${suffix}`;
            suffix++;
        }

        usedIds.add(id);
        return id;
    }
}

new NewsVideoModal();
document.querySelectorAll('.youtubeThumbnail').forEach(image => new YouTubeThumbnail(image));
document.querySelectorAll('.page-news-single').forEach(section => new ArticleTableOfContents(section));

window.ArticleTableOfContents = ArticleTableOfContents;
